import type { Thresholds } from '../../core/thresholds';
import type { PeReport } from '../ProgramEvaluation/types';
import { formatRate } from '../ProgramEvaluation/utils/formatGrowth';
import { countSisMatched } from './sisMatchCount';
import type { SisStudent } from '../Upload/sisRoster';
import { findSisInRoster, loadSisRoster, loadSisRosterMapped } from '../Upload/sisRoster';
import {
  accessScaleOnly,
  type WidaStudent,
  type WidaYearRecord,
} from '../Upload/widaHistory';
import { loadWidaRoster } from '../Upload/studentRoster';
import {
  WIDA_RESET_SPRING_YEAR,
  isGateBSatisfied,
  isGateCSatisfied,
  type DashboardGateInputs,
} from './gates';
import { WIDA_LEVEL_KEYS, type DomainLevelCounts } from './domainBars';
import type { SchoolRow } from './schools';
import type { DashboardData } from './index';
import type { DataHealthRow } from './components/DataHealth';

export const CUT_SCORES_FINAL = false;

export interface DashboardSources {
  report: PeReport;
  roster: WidaStudent[];
  sis: SisStudent[];
  sisFieldsMapped: boolean;
  cutScoresFinal: boolean;
  thresholds: Thresholds;
  // The server's verdict that the artifact no longer matches its sources
  // (a newer upload, or the roster feed moved or was unbound).
  stale?: boolean;
}

export function gatherDashboardSources(report: PeReport, stale: boolean, thresholds: Thresholds): DashboardSources {
  return {
    report,
    roster: accessScaleOnly(loadWidaRoster()),
    sis: loadSisRoster(),
    sisFieldsMapped: loadSisRosterMapped(),
    cutScoresFinal: CUT_SCORES_FINAL,
    thresholds,
    stale,
  };
}

function latestUploadStartYear(roster: WidaStudent[]): number | null {
  const years = roster.flatMap((student) => student.years.map((record) => record.year));
  return years.length === 0 ? null : Math.max(...years) - 1;
}

export function buildDashboardData(sources: DashboardSources): DashboardData | null {
  const { report, roster, sis } = sources;
  if (report.cannot_generate) {
    return null;
  }
  if (report.assessment_data_absent) {
    // A WIDA upload after a roster-only generation, or a feed that moved
    // since, makes that artifact stale; until it is regenerated the
    // dashboard falls back like any other stale report.
    return roster.length === 0 && !sources.stale ? rosterOnlyDashboard(sources) : null;
  }
  const artifactStart = Number(report.district.school_year.slice(0, 4));
  const uploadStart = latestUploadStartYear(roster);
  if (uploadStart == null || !Number.isFinite(artifactStart) || artifactStart < uploadStart) {
    return null;
  }
  const gateInputs: DashboardGateInputs = {
    cutScoresFinal: sources.cutScoresFinal,
    accessAdministrationSpringYears: scoredSpringYears(roster),
    sisFieldsMapped: sis.length > 0 && sources.sisFieldsMapped,
  };
  const gateB = isGateBSatisfied(gateInputs);
  const gateC = isGateCSatisfied(gateInputs);
  const snapshot = report.metadata.district_config_snapshot;
  const speakingMinimum = snapshot?.DomainMinimums?.['speaking'];

  return {
    programOverview: {
      districtName: report.district.name,
      elStudentCount: report.district.el_student_count,
      grades: gradeBands(roster, sis),
      ltelCount: gateC ? report.ltel.count : estimatedLtelCount(roster, sources.thresholds),
      ltelEstimated: !gateC,
      domains: domainDistribution(roster),
      growthTrendAvailable: gateB,
    },
    thresholds:
      snapshot && speakingMinimum != null
        ? { composite: snapshot.CompositeMinimum, speaking: speakingMinimum }
        : undefined,
    programEffectiveness: gateC ? programEffectiveness(report) : undefined,
    schools: gateC ? schoolRows(report) : undefined,
    dataHealth: {
      sources: [widaSourceRow(roster), flashlightSourceRow(report), sisSourceRow(sources, gateC)],
      quality: [studentMatchingRow(roster, sis)],
    },
  };
}

// A report built from the roster feed alone: no WIDA upload to date the
// report against or to draw domains and growth from, while enrollment,
// exits and LTEL counts come from the report itself.
function rosterOnlyDashboard(sources: DashboardSources): DashboardData {
  const { report } = sources;
  const snapshot = report.metadata.district_config_snapshot;
  const speakingMinimum = snapshot?.DomainMinimums?.['speaking'];
  return {
    programOverview: {
      districtName: report.district.name,
      elStudentCount: report.district.el_student_count,
      grades: null,
      ltelCount: report.ltel.count,
      ltelEstimated: false,
      domains: null,
      growthTrendAvailable: false,
    },
    thresholds:
      snapshot && speakingMinimum != null
        ? { composite: snapshot.CompositeMinimum, speaking: speakingMinimum }
        : undefined,
    programEffectiveness: programEffectiveness(report),
    schools: schoolRows(report),
    dataHealth: {
      sources: [
        widaSourceRow([]),
        flashlightSourceRow(report),
        { name: 'SIS', detail: 'Roster feed connected', pill: 'Synced' },
      ],
      quality: tenureCoverageRows(report),
    },
  };
}

// The report says when some students carry no service start date; the
// LTEL and newcomer counts on this page cover only the ones who do.
function tenureCoverageRows(report: PeReport): DataHealthRow[] {
  const unknown = report.data_quality.students_with_unknown_tenure ?? 0;
  if (unknown === 0) return [];
  return [
    {
      name: 'Time in program',
      detail: `${unknown} ${unknown === 1 ? 'student carries' : 'students carry'} no EL service start date on the roster feed; LTEL and newcomer counts cover only students with one`,
    },
  ];
}

function isScoredAdmin(record: WidaYearRecord): boolean {
  if (record.waived) return false;
  return DOMAIN_FIELDS.some(({ field }) => record[field].level != null);
}

function scoredSpringYears(roster: WidaStudent[]): number[] {
  return [
    ...new Set(
      roster.flatMap((student) => {
        const years = [
          ...new Set(
            student.years
              .filter(isScoredAdmin)
              .map((record) => record.year)
              .filter((year) => year >= WIDA_RESET_SPRING_YEAR),
          ),
        ];
        return years.length >= 2 ? years : [];
      }),
    ),
  ];
}

function estimatedLtelCount(roster: WidaStudent[], thresholds: Thresholds): number {
  return roster.filter((s) => s.yearsInEl != null && s.yearsInEl >= thresholds.ltelMinYears).length;
}

const GRADE_ZERO = new Set(['K', 'KG', 'KINDERGARTEN', 'PK', 'PRE-K', 'PREK']);

function parseGradeLabel(value: string): number | null {
  const v = value.trim().toUpperCase();
  if (GRADE_ZERO.has(v)) return 0;
  return /^\d+$/.test(v) ? Number(v) : null;
}

function gradeBands(
  roster: WidaStudent[],
  sis: SisStudent[],
): { k5: number; g68: number; g912: number } {
  const bands = { k5: 0, g68: 0, g912: 0 };
  const add = (grade: number | null) => {
    if (grade == null || grade > 12) return;
    if (grade <= 5) bands.k5 += 1;
    else if (grade <= 8) bands.g68 += 1;
    else bands.g912 += 1;
  };
  const matched = new Set<SisStudent>();
  for (const student of roster) {
    const match = findSisInRoster(
      [student.stateStudentId, student.localStudentId, student.id],
      sis,
    );
    if (match) matched.add(match);
    add((match ? parseGradeLabel(match.grade) : null) ?? parseGradeLabel(student.latestGrade));
  }
  for (const record of sis) {
    if (!matched.has(record)) add(parseGradeLabel(record.grade));
  }
  return bands;
}

const DOMAIN_FIELDS = [
  { domain: 'Listening', field: 'listening' },
  { domain: 'Speaking', field: 'speaking' },
  { domain: 'Reading', field: 'reading' },
  { domain: 'Writing', field: 'writing' },
] as const;

function emptyCounts(): DomainLevelCounts {
  return Object.fromEntries(WIDA_LEVEL_KEYS.map((key) => [key, 0])) as DomainLevelCounts;
}

function latestScoredAdmin(student: WidaStudent): WidaYearRecord | null {
  let latest: WidaYearRecord | null = null;
  for (const record of student.years) {
    if (!isScoredAdmin(record)) continue;
    if (latest === null || record.year > latest.year) latest = record;
  }
  return latest;
}

function domainDistribution(roster: WidaStudent[]): { domain: string; counts: DomainLevelCounts }[] {
  const distribution = DOMAIN_FIELDS.map(({ domain }) => ({ domain, counts: emptyCounts() }));
  for (const student of roster) {
    const admin = latestScoredAdmin(student);
    if (!admin) continue;
    DOMAIN_FIELDS.forEach(({ field }, index) => {
      const level = admin[field].level;
      if (level == null || level < 1) return;
      const key = WIDA_LEVEL_KEYS[Math.min(Math.floor(level), WIDA_LEVEL_KEYS.length) - 1]!;
      distribution[index]!.counts[key] += 1;
    });
  }
  return distribution;
}

function programEffectiveness(report: PeReport) {
  const thisYear = report.reclassification.this_year;
  const median = report.reclassification.median_years_in_program_for_reclassifiers;
  const exits = report.enrollment.departures_this_year ?? report.enrollment.exits_this_year;
  return {
    reclassificationRate: {
      percent:
        thisYear.reclassified_count > 0 && thisYear.rate !== null
          ? formatRate(thisYear.rate)
          : undefined,
      ...(thisYear.rate === null ? { unavailable: true } : {}),
      detail:
        thisYear.eligible_count === null
          ? `${thisYear.reclassified_count} EL students exited this year`
          : `${thisYear.reclassified_count} of ${thisYear.eligible_count} eligible EL students exited this year`,
    },
    exits: {
      reclassified: exits.reclassified,
      other: exits.moved_out + exits.other,
      derived: exits.derived_count ?? 0,
    },
    medianTime: {
      value: median != null ? `${Math.round(median * 10) / 10} years` : 'N/A',
      detail: 'in EL services before exiting',
    },
  };
}

function schoolRows(report: PeReport): SchoolRow[] {
  return report.by_school.map((school) => ({
    school: school.school_name,
    students: school.el_count,
    newcomers: school.newcomer_count,
    ltels: school.ltel_count,
  }));
}

function widaSourceRow(roster: WidaStudent[]): DataHealthRow {
  const years = roster.flatMap((student) => student.years.map((record) => record.year));
  if (years.length === 0) {
    return { name: 'WIDA ACCESS', detail: 'No assessment data.', action: { label: 'Upload data' } };
  }
  return { name: 'WIDA ACCESS', detail: `Spring ${Math.max(...years)}`, pill: 'Uploaded' };
}

function formatSyncDate(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function flashlightSourceRow(report: PeReport): DataHealthRow {
  if (!report.flashlight.is_integrated) {
    return {
      name: 'Flashlight',
      detail: "Flashlight isn't connected.",
      action: { label: 'Connect' },
    };
  }
  const synced = formatSyncDate(report.metadata.generated_at);
  return {
    name: 'Flashlight',
    detail: synced ? `Synced ${synced}` : 'Synced',
    pill: 'Connected',
  };
}

function sisSourceRow(sources: DashboardSources, gateC: boolean): DataHealthRow {
  if (gateC) {
    return { name: 'SIS', detail: 'Uploaded', pill: 'Mapped' };
  }
  if (sources.sis.length > 0) {
    return {
      name: 'SIS',
      detail:
        'Map your SIS columns to unlock newcomer counts, precise LTEL, monitoring-window, and school breakdowns.',
      action: { label: 'Set up SIS', route: '/upload' },
    };
  }
  return {
    name: 'SIS',
    detail:
      'Not uploaded. Upload and map columns to unlock newcomer and LTEL counts, monitoring-window, and school breakdowns',
    action: { label: 'Upload', route: '/upload' },
  };
}

function studentMatchingRow(roster: WidaStudent[], sis: SisStudent[]): DataHealthRow {
  const matched = countSisMatched(roster, sis);
  const unmatched = roster.length - matched;
  if (unmatched === 0) {
    return {
      name: 'Student matching',
      detail: `${matched} matched · no matches to review`,
      pill: 'Matched',
    };
  }
  return {
    name: 'Student matching',
    detail: `${matched} matched · ${unmatched} unmatched`,
    action: { label: 'Review matches' },
  };
}
